import {
  MODOOCAR_BASE_URL,
  MODOOCAR_RANK_ROUTE,
  MYCLE_HOME_URL,
} from '@/app/(makers)/modoocar/_constants'
import { IS_PRODUCTION } from '@/shared/lib'

import type {
  IRankingList,
  IRankingQuery,
  IRankSegment,
  TApiRankSort,
  TRankFuelType,
  TRankTab,
} from '../_types'
import type { TVehicleType } from '@/app/(makers)/modoocar/_types'

export { MODOOCAR_BASE_URL, MYCLE_HOME_URL }

interface IRankTabOption {
  id: TRankTab
  label: string
}

interface IGetRankReferenceNoteParams {
  tab: TRankTab
  variant: 'mobile' | 'desktop'
  ranking: Pick<
    IRankingList,
    'referenceDate' | 'annualMileage' | 'oilPriceDate' | 'evPriceDate'
  >
}

interface IBuildModoocarDetailPathParams {
  brandName: string
  modelName: string
  fuelType?: string
  vehicleType?: TVehicleType
  /** [주의]: 커스텀 세그먼트일 때만 붙입니다. 항상 붙이면 기본 상세 URL(사이트맵·canonical)이 바뀝니다. */
  segment?: string
}

type TRankCanonicalParams = Pick<IRankingQuery, 'tab' | 'segment'>

export const RANK_TABS: IRankTabOption[] = [
  { id: 'score', label: '만족도순' },
  { id: 'efficiency', label: '실연비순' },
  { id: 'fuelCost', label: '유류비순' },
]

export const RANK_TAB_TITLE: Record<TRankTab, string> = {
  score: '오너 만족도 랭킹',
  efficiency: '실연비 랭킹',
  fuelCost: '연간 유류비 랭킹',
}

export const RANK_TAB_DESCRIPTION: Record<TRankTab, string> = {
  score:
    '마이클 앱 오너들의 실측 데이터를 기반으로\n오너들의 실제 만족도 점수를 비교합니다.',
  efficiency:
    '마이클 앱 오너들의 실측 데이터를 기반으로\n오너들의 실제 연비를 비교합니다.',
  fuelCost:
    '마이클 앱 오너들의 실연비와\n연 평균 주행거리를 기준으로 유류비를 비교합니다.',
}

export const RANK_ELECTRIC_EFFICIENCY_TAB_LABEL = '실전비순'
export const RANK_ELECTRIC_EFFICIENCY_TITLE = '실전비 랭킹'
export const RANK_ELECTRIC_EFFICIENCY_DESCRIPTION =
  '마이클 앱 오너들의 실측 데이터를 기반으로\n오너들의 실제 전비를 비교합니다.'

export const getRankTabLabel = (
  tab: IRankTabOption,
  isElectric: boolean
): string =>
  tab.id === 'efficiency' && isElectric
    ? RANK_ELECTRIC_EFFICIENCY_TAB_LABEL
    : tab.label

export const getRankTabTitle = (tab: TRankTab, isElectric: boolean): string =>
  tab === 'efficiency' && isElectric
    ? RANK_ELECTRIC_EFFICIENCY_TITLE
    : RANK_TAB_TITLE[tab]

export const getRankTabDescription = (
  tab: TRankTab,
  isElectric: boolean,
  annualMileage = 0
): string => {
  if (tab === 'fuelCost') {
    return `마이클 앱 오너들의 실연비와\n연 평균 주행거리 ${formatAnnualMileage(annualMileage)} 기준으로 유류비를 비교합니다.`
  }
  if (tab === 'efficiency' && isElectric) {
    return RANK_ELECTRIC_EFFICIENCY_DESCRIPTION
  }
  return RANK_TAB_DESCRIPTION[tab]
}

const ANNUAL_MILEAGE_UNIT = 10_000

const formatAnnualMileage = (km: number): string =>
  `${(km / ANNUAL_MILEAGE_UNIT).toLocaleString()}만 km`

export const getRankReferenceNote = ({
  tab,
  variant,
  ranking,
}: IGetRankReferenceNoteParams): string => {
  if (tab === 'fuelCost') {
    const mileage = `연 평균 주행거리 ${formatAnnualMileage(ranking.annualMileage)}`
    const sources: string[] = []
    if (ranking.oilPriceDate) {
      sources.push(`오피넷 ${ranking.oilPriceDate} 유가 기준`)
    }
    if (ranking.evPriceDate) {
      sources.push(`기후에너지환경부의 ${ranking.evPriceDate} 전기 요금 기준`)
    }
    return sources.length > 0 ? `${mileage}, ${sources.join('·')}` : mileage
  }

  const suffix =
    variant === 'mobile' ? '마이클 실사용 데이터 기준' : '마이클 데이터 기준'
  return `${ranking.referenceDate} ${suffix}`
}

/** [백엔드]: 커스텀 세그먼트는 백엔드가 차종명·연료 규칙으로 따로 뽑아서 크기 세그먼트와 겹칩니다. 상세로 들어갈 때는 이 값만 summary에 넘깁니다. */
export const RANK_CUSTOM_SEGMENTS = [
  '하이브리드',
  '플러그인 하이브리드',
  '전기차',
] as const

export const resolveCustomSegment = (raw: unknown): string | undefined =>
  RANK_CUSTOM_SEGMENTS.find(segment => segment === raw)

export const RANK_SEGMENTS: IRankSegment[] = [
  { id: '전체', label: '전체' },
  { id: '국내 소형SUV', label: '국내 소형SUV' },
  { id: '국내 중형SUV', label: '국내 중형SUV' },
  { id: '국내 대형SUV', label: '국내 대형SUV' },
  { id: '국내 경차', label: '국내 경차' },
  { id: '국내 소형', label: '국내 소형' },
  { id: '국내 준중형', label: '국내 준중형' },
  { id: '국내 중형', label: '국내 중형' },
  { id: '국내 준대형', label: '국내 준대형' },
  { id: '국내 대형', label: '국내 대형' },
  ...RANK_CUSTOM_SEGMENTS.map(id => ({ id, label: id })),
  { id: '수입 소형SUV', label: '수입 소형SUV' },
  { id: '수입 중형SUV', label: '수입 중형SUV' },
  { id: '수입 대형SUV', label: '수입 대형SUV' },
  { id: '수입 소형', label: '수입 소형' },
  { id: '수입 준중형', label: '수입 준중형' },
  { id: '수입 중형', label: '수입 중형' },
  { id: '수입 준대형', label: '수입 준대형' },
  { id: '수입 대형', label: '수입 대형' },
  { id: '국내 RV/MPV', label: '국내 RV/MPV' },
  { id: '국내 상용', label: '국내 상용' },
  { id: '국내 스포츠카', label: '국내 스포츠카' },
  { id: '수입 스포츠카', label: '수입 스포츠카' },
  { id: '수입 RV/MPV', label: '수입 RV/MPV' },
  { id: '수입 상용', label: '수입 상용' },
  { id: '수입 경차', label: '수입 경차' },
]

export const RANK_ELECTRIC_SEGMENT = '전기차'

export const isRankElectricSegment = (segment: string): boolean =>
  segment === RANK_ELECTRIC_SEGMENT

export const RANK_ELECTRIC_FUEL_TYPES: TRankFuelType[] = ['전기']

/** [정책]: 고급 휘발유는 휘발유에 포함해서 따로 두지 않습니다. */
export const RANK_NON_ELECTRIC_FUEL_TYPES: TRankFuelType[] = [
  '휘발유',
  '디젤',
  'LPG',
]

export const getRankFuelOptions = (segment: string): TRankFuelType[] =>
  isRankElectricSegment(segment)
    ? RANK_ELECTRIC_FUEL_TYPES
    : RANK_NON_ELECTRIC_FUEL_TYPES

export const RANK_FUEL_DOMAIN_TO_API: Record<TRankFuelType, string> = {
  휘발유: 'GASOLINE_REGULAR',
  디젤: 'DIESEL',
  LPG: 'LPG',
  전기: 'EV',
}

export const RANK_FUEL_TYPES = Object.keys(
  RANK_FUEL_DOMAIN_TO_API
) as TRankFuelType[]

export const toRankFuelType = (
  apiFuelType: string
): TRankFuelType | undefined =>
  RANK_FUEL_TYPES.find(
    fuelType => RANK_FUEL_DOMAIN_TO_API[fuelType] === apiFuelType
  )

export const RANK_TAB_TO_SORT: Record<TRankTab, TApiRankSort> = {
  score: 'SCORE',
  efficiency: 'EFFICIENCY',
  fuelCost: 'FUEL_COST',
}

export const RANK_DEFAULT_VEHICLE_TYPE = '자동차'

export const RANK_API_VEHICLE_TYPE = 'CAR'

/** [정책]: 이 순위 다음부터는 앱 설치를 유도합니다. */
export const RANK_VISIBLE_LIMIT = 10

export const RANK_EVENT = {
  PAGE_SHOW: 'common_web_modoocar_page_show',
  SECTION_SHOW: 'common_web_modoocar_section_show',
  ITEM_SHOW: 'common_web_modoocar_item_show',
  ITEM_CLICK: 'common_web_modoocar_item_click',
  ITEM_CHANGE: 'common_web_modoocar_item_change',
  LOGO_CLICK: 'common_web_modoocar_logo_click',
} as const

export const RANK_TRACK_CODE = {
  PAGE: 'page',
  HEADER: 'header',
  TAB: 'tab',
  CATEGORY: 'category',
  SUMMARY: 'summary',
  RANK_LIST: 'rank_list',
  RANK_MORE: 'rank_more',
  // [정책]: 랭킹에서 들어온 설치를 따로 집계하려고 상세 하단바(bottom_cta)와 code를 나눕니다.
  BOTTOM_CTA: 'rank_bottom_cta',
  SHARE: 'share',
} as const

export const RANK_TYPE_LABEL: Record<TRankTab, string> = {
  score: '만족도',
  efficiency: '실연비',
  fuelCost: '유류비',
}

/** [정책]: 모바일은 SmartScript 동적 OneLink(useSeoOnelinkUrl)를 먼저 쓰고, 이 값은 데스크탑 랜딩과 모바일 폴백에 씁니다. */
export const RANK_CTA_ONELINK_URL = IS_PRODUCTION
  ? 'https://web-seo-geo.onelink.me/kFy7/dpiq1sqx'
  : 'https://web-dev-seo-geo.onelink.me/hCvn/sw6zm1r7'

/** [라이브러리]: Next.js가 canonical의 `'`를 %27로 바꿔서 사이트맵과 내부 링크도 같게 맞춥니다(예: 야마하 BW'S). */
const encodeParam = (value: string) =>
  encodeURIComponent(value).replace(/'/g, '%27')

/** [백엔드]: 유류비순은 항목마다 유종이 달라서, fuelType 없이 조회하면 일부 차종이 커스텀 차량으로 잘못 매칭됩니다. */
export const buildModoocarDetailPath = ({
  brandName,
  modelName,
  fuelType,
  vehicleType,
  segment,
}: IBuildModoocarDetailPathParams): string => {
  const vehicleSegment =
    vehicleType === 'MOTORCYCLE' ? '모터사이클' : RANK_DEFAULT_VEHICLE_TYPE
  const customSegment = resolveCustomSegment(segment)
  return (
    `/modoocar/${encodeURIComponent(vehicleSegment)}` +
    `?brandName=${encodeParam(brandName)}&modelName=${encodeParam(modelName)}` +
    (fuelType ? `&fuelType=${encodeParam(fuelType)}` : '') +
    (customSegment ? `&segment=${encodeParam(customSegment)}` : '')
  )
}

export const buildModoocarDetailUrl = (
  params: Omit<IBuildModoocarDetailPathParams, 'segment'>
): string => `${MYCLE_HOME_URL}${buildModoocarDetailPath(params)}`

export const RANK_OG_IMAGE_ROUTE = '/api/modoocar/rank-og'

export const RANK_OG_IMAGE_SIZE = { width: 1080, height: 600 } as const

/** [라이브러리]: catch-all 페이지에는 opengraph-image 파일을 둘 수 없어서 별도 라우트가 조합별 이미지를 만듭니다. */
export const buildRankOgImageUrl = ({
  tab,
  segment,
  fuelType,
}: IRankingQuery): string =>
  `${MYCLE_HOME_URL}${RANK_OG_IMAGE_ROUTE}?tab=${tab}&segment=${encodeURIComponent(segment)}&fuelType=${encodeURIComponent(fuelType)}`

export const RANK_QUERY_KEY = {
  TAB: 'tab',
  SEGMENT: 'segment',
  FUEL_TYPE: 'fuelType',
} as const

export const RANK_DEFAULT_TAB: TRankTab = 'score'
export const RANK_DEFAULT_SEGMENT = '전체'
export const RANK_DEFAULT_FUEL_TYPE: TRankFuelType = '휘발유'

export const getRankDefaultFuelType = (segment: string): TRankFuelType =>
  isRankElectricSegment(segment) ? '전기' : RANK_DEFAULT_FUEL_TYPE

/** [정책]: 크기 세그먼트에도 전기차가 있을 수 있어서 아는 유종은 그대로 두고, 데이터가 없는 유종은 빈 상태로 보여 줍니다. */
export const resolveRankFuelType = (
  raw: string | undefined,
  segment: string
): TRankFuelType => {
  if (isRankElectricSegment(segment)) return '전기'
  return RANK_FUEL_TYPES.includes(raw as TRankFuelType)
    ? (raw as TRankFuelType)
    : RANK_DEFAULT_FUEL_TYPE
}

export const resolveRankTab = (raw?: string): TRankTab =>
  RANK_TABS.some(tab => tab.id === raw) ? (raw as TRankTab) : RANK_DEFAULT_TAB

export const resolveRankSegment = (raw?: string): string =>
  RANK_SEGMENTS.some(segment => segment.id === raw)
    ? (raw as string)
    : RANK_DEFAULT_SEGMENT

export const parseRankFilters = (filters?: string[]): IRankingQuery => {
  const tab = resolveRankTab(filters?.[0])
  const segment =
    tab === 'fuelCost' ? RANK_DEFAULT_SEGMENT : resolveRankSegment(filters?.[1])
  const fuelType = resolveRankFuelType(filters?.[2], segment)
  return { tab, segment, fuelType }
}

/** [정책]: 유종은 색인 단위가 아니라서 탭·차급 경로로 합칩니다. */
export const buildRankCanonicalPath = ({
  tab,
  segment,
}: TRankCanonicalParams): string =>
  tab === RANK_DEFAULT_TAB && segment === RANK_DEFAULT_SEGMENT
    ? MODOOCAR_RANK_ROUTE
    : `${MODOOCAR_RANK_ROUTE}/${tab}/${encodeURIComponent(segment)}`

export const buildRankPath = ({
  tab,
  segment,
  fuelType,
}: IRankingQuery): string =>
  fuelType === getRankDefaultFuelType(segment)
    ? buildRankCanonicalPath({ tab, segment })
    : `${MODOOCAR_RANK_ROUTE}/${tab}/${encodeURIComponent(segment)}/${encodeURIComponent(fuelType)}`

export const getRankSitemapPaths = (): string[] =>
  RANK_TABS.flatMap(tab =>
    // [정책]: 유류비는 차급 구분이 없어서 '전체' 한 건만 색인합니다.
    (tab.id === 'fuelCost'
      ? [RANK_DEFAULT_SEGMENT]
      : RANK_SEGMENTS.map(segment => segment.id)
    ).map(segmentId =>
      buildRankCanonicalPath({ tab: tab.id, segment: segmentId })
    )
  )

export const getRankStaticFilters = (): { filters: string[] }[] =>
  RANK_TABS.flatMap(tab => {
    if (tab.id === 'fuelCost') {
      return [{ filters: [tab.id, RANK_DEFAULT_SEGMENT] }]
    }
    return RANK_SEGMENTS.map(segment => {
      const isDefaultView =
        tab.id === RANK_DEFAULT_TAB && segment.id === RANK_DEFAULT_SEGMENT
      return {
        filters: isDefaultView ? [] : [tab.id, segment.id],
      }
    })
  })
